import React, { useState } from 'react';
import { VoucherAccommodation, VoucherHotel } from 'services/ItineraryManagerApi/types';
import { formatDateDisplay, isBlank } from 'utils';
import Editor from 'pureUi/Editor';
import Checkbox from 'ui/Checkbox';
import { SlideToggle } from 'ui/SlideToggle';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import { useModal } from 'hooks/useModal';
import { VoucherAddUploadModal } from '../VoucherAddUploadModal';
import { CircleIconButton } from 'ui/CircleIconButton';
import FluidButton from 'ui/FluidButton';
import XIcon from 'ui/Icons/tabler/x.component.svg';
import classNames from 'classnames';
import { UITextArea } from 'ui/UITextArea';

interface IVoucherAccommodationEditProps {
  hotel: VoucherHotel;
  accommodation: VoucherAccommodation;
  voucherUuid: string;
  onVisibilityToggle: (hotelUuid: string, accommodationUuid: string, isVisible: boolean) => void;
  onDescriptionChange: (hotelUuid: string, accommodationUuid: string, description: string) => void;
  onMoreTextChange: (hotelUuid: string, accommodationUuid: string, moreText: string) => void;
  onMealPlanDescriptionChange: (
    hotelUuid: string,
    accommodationUuid: string,
    mealPlanUuid: string,
    description: string
  ) => void;
  onSupplementVisibilityToggle: (
    hotelUuid: string,
    accommodationUuid: string,
    supplementUuid: string,
    isVisible: boolean
  ) => void;
  onSupplementTertiaryTextChange: (
    hotelUuid: string,
    accommodationUuid: string,
    supplementUuid: string,
    tertiaryText: string
  ) => void;
  onAdditionalServiceVisibilityToggle: (
    hotelUuid: string,
    accommodationUuid: string,
    serviceUuid: string,
    isVisible: boolean
  ) => void;
  onAdditionalServiceTertiaryTextChange: (
    hotelUuid: string,
    accommodationUuid: string,
    serviceUuid: string,
    tertiaryText: string
  ) => void;
  onSupplementsMoreInfoChange: (hotelUuid: string, accommodationUuid: string, text: string) => void;
  onAdditionalServicesMoreInfoChange: (hotelUuid: string, accommodationUuid: string, text: string) => void;
  onRateFeaturesVisibilityToggle: (hotelUuid: string, accommodationUuid: string, isVisible: boolean) => void;
  onAccommodationAddUpload: (hotelUuid: string, accommodationUuid: string, uploadData: any) => void;
  onAccommodationRemoveUpload: (hotelUuid: string, accommodationUuid: string, uploadIndex: number) => void;
  onMealPlanAddUpload: (hotelUuid: string, accommodationUuid: string, mealPlanUuid: string, uploadData: any) => void;
  onMealPlanRemoveUpload: (
    hotelUuid: string,
    accommodationUuid: string,
    mealPlanUuid: string,
    uploadIndex: number
  ) => void;
}

export const VoucherAccommodationEdit: React.FC<IVoucherAccommodationEditProps> = ({
  hotel,
  accommodation,
  voucherUuid,
  onVisibilityToggle,
  onDescriptionChange,
  onMoreTextChange,
  onMealPlanDescriptionChange,
  onSupplementVisibilityToggle,
  onSupplementTertiaryTextChange,
  onAdditionalServiceVisibilityToggle,
  onAdditionalServiceTertiaryTextChange,
  onSupplementsMoreInfoChange,
  onAdditionalServicesMoreInfoChange,
  onRateFeaturesVisibilityToggle,
  onAccommodationAddUpload,
  onAccommodationRemoveUpload,
  onMealPlanAddUpload,
  onMealPlanRemoveUpload,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openSupplements, setOpenSupplements] = useState<string[]>([]);
  const [openMealPlans, setOpenMealPlans] = useState<string[]>([]);
  const [openAdditionalServices, setOpenAdditionalServices] = useState<string[]>([]);

  const handleSupplementToggle = (supplementUuid: string, isOpen: boolean) => {
    if (isOpen) {
      setOpenSupplements([...openSupplements, supplementUuid]);
    } else {
      setOpenSupplements(openSupplements.filter(uuid => uuid !== supplementUuid));
    }
  };

  const handleMealPlanToggle = (mealPlanUuid: string, isOpen: boolean) => {
    if (isOpen) {
      setOpenMealPlans([...openMealPlans, mealPlanUuid]);
    } else {
      setOpenMealPlans(openMealPlans.filter(uuid => uuid !== mealPlanUuid));
    }
  };

  const handleAdditionalServiceToggle = (serviceUuid: string, isOpen: boolean) => {
    if (isOpen) {
      setOpenAdditionalServices([...openAdditionalServices, serviceUuid]);
    } else {
      setOpenAdditionalServices(openAdditionalServices.filter(uuid => uuid !== serviceUuid));
    }
  };

  const accommodationUploadModalOps = useModal<{
    uploadType: 'image' | 'file';
  }>();

  const mealPlanUploadModalOps = useModal<{
    uploadType: 'image' | 'file';
    mealPlanUuid: string;
  }>();

  // Calculate nights for this specific accommodation
  const accStartDate = new Date(accommodation.startDate);
  const accEndDate = new Date(accommodation.endDate);
  const accNights = Math.ceil((accEndDate.getTime() - accStartDate.getTime()) / (1000 * 60 * 60 * 24));

  return (
    <>
      <AccordionStackable
        isOpen={isOpen}
        onToggle={setIsOpen}
        renderSummary={({ isOpen }) => (
          <span className="flex items-center justify-between w-full pr-4">
            <span
              className={classNames('text-base font-pt-sans', {
                'font-bold': isOpen,
                'font-medium': !isOpen,
              })}
            >
              {accommodation.title}
            </span>
            <SlideToggle
              id={`toggle-accommodation-${accommodation.uuid}`}
              checked={accommodation.isVisible}
              onActivate={() => onVisibilityToggle(hotel.uuid, accommodation.uuid, true)}
              onDeactivate={() => onVisibilityToggle(hotel.uuid, accommodation.uuid, false)}
              labelClassName={!accommodation.isVisible ? 'bg-gray-20' : 'bg-teal-40'}
              buttonClassName={!accommodation.isVisible ? 'bg-gray-100' : 'bg-teal-100'}
              onClick={e => e.stopPropagation()}
            />
          </span>
        )}
      >
        {/* Room Details Header */}
        <div className="mt-4 mb-4">
          {/* Confirmation Number */}
          {!isBlank(accommodation.confirmationNumber?.trim()) && (
            <div className="flex justify-end mb-2">
              <span className="font-pt-sans text-13px text-gray-100 bg-white-hard py-1 px-2">
                Confirm N. {accommodation.confirmationNumber}
              </span>
            </div>
          )}

          {/* Booking Details */}
          <div className="flex justify-end">
            <div className="text-right">
              <span className="font-pt-sans text-gray-100 text-sm">
                {formatDateDisplay(accommodation.startDate)} - {formatDateDisplay(accommodation.endDate)}
                <span className="ml-2 bg-teal-20 text-black px-2 py-1 text-xs rounded">{accNights} Nights</span>
                <span className="ml-4">
                  {accommodation.numberOfAdults && `${accommodation.numberOfAdults} x Adult`}
                  {accommodation.agesOfAllChildren &&
                    accommodation.agesOfAllChildren.length > 0 &&
                    ` ${accommodation.agesOfAllChildren.length} x Child`}
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 space-y-4">
          {/* Room description textarea */}
          <div className="mb-4">
            <UITextArea
              label="Description"
              labelClassName="text-sm font-pt-sans text-gray-100 mb-2"
              value={accommodation.description || ''}
              onChange={value => onDescriptionChange(hotel.uuid, accommodation.uuid, value)}
              placeholder="Write room description..."
              minRows={1}
              autoResize={true}
            />
          </div>

          {/* Room moreText RTE */}
          <div className="mb-4 relative">
            <p className="text-sm font-pt-sans text-gray-100 mb-2">More Text</p>
            <Editor
              handleEditorChange={content => onMoreTextChange(hotel.uuid, accommodation.uuid, content)}
              text={accommodation.moreText || ''}
              options={[]}
              placeHolder="Write additional room information..."
              minHeight="125px"
              initVals={{
                autoresize_bottom_margin: 0,
                min_height: 125,
              }}
            />
          </div>

          {/* Accommodation Attachments */}
          {accommodation.uploads.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-pt-sans text-gray-100 mb-2">Attachments</p>
              <div className="flex gap-2 flex-wrap">
                {accommodation.uploads.map((upload, uploadIdx) => (
                  <div key={uploadIdx} className="relative">
                    <span className="bg-gray-10 text-12px text-gray-100 p-10px w-[90px] h-[90px] flex items-center">
                      <span className="w-full line-clamp-3">{upload.displayName}</span>
                    </span>
                    <CircleIconButton
                      className="absolute top-2 right-2"
                      IconComponent={XIcon}
                      iconSize="sm"
                      type="secondary"
                      onClick={() => onAccommodationRemoveUpload(hotel.uuid, accommodation.uuid, uploadIdx)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add attachment button */}
          <div className="mb-4">
            <FluidButton
              onClick={async () => {
                const result = await accommodationUploadModalOps.requestConfirmation({
                  uploadType: 'file',
                });
                if (result) {
                  onAccommodationAddUpload(hotel.uuid, accommodation.uuid, result);
                }
              }}
              type="secondary"
            >
              Attach File
            </FluidButton>
          </div>

          {/* Rate Inclusions */}
          {accommodation.rateFeatures && accommodation.rateFeatures.length > 0 && (
            <div className="py-[10px]">
              <label className="flex items-center gap-2 cursor-pointer w-fit">
                <Checkbox
                  checked={accommodation.isRateFeaturesVisible ?? true}
                  onChange={e => onRateFeaturesVisibilityToggle(hotel.uuid, accommodation.uuid, (e.target as HTMLInputElement).checked)}
                  color="green-prime"
                />
                <span className="font-pt-sans text-base font-normal text-gray-100">Show Rate Inclusions</span>
              </label>
            </div>
          )}

          {/* Meal Plans */}
          {accommodation.mealPlans.length > 0 && (
            <div className="mb-4">
              {accommodation.mealPlans.map(mealPlan => (
                <AccordionStackable
                  key={mealPlan.uuid}
                  isOpen={openMealPlans.includes(mealPlan.uuid)}
                  onToggle={isOpen => handleMealPlanToggle(mealPlan.uuid, isOpen)}
                  renderSummary={({ isOpen }) => (
                    <span className="flex items-center justify-between w-full pr-4">
                      <span
                        className={classNames('text-base font-pt-sans', {
                          'font-bold': isOpen,
                          'font-medium': !isOpen,
                        })}
                      >
                        Meal Plan: {mealPlan.name}
                      </span>
                    </span>
                  )}
                >
                  <div className="space-y-4">
                    <div className="relative">
                      <p className="text-sm font-pt-sans text-gray-100 mb-2">Description</p>
                      <Editor
                        handleEditorChange={content =>
                          onMealPlanDescriptionChange(hotel.uuid, accommodation.uuid, mealPlan.uuid, content)
                        }
                        text={mealPlan.description || ''}
                        options={[]}
                        placeHolder="Write meal plan description..."
                        minHeight="125px"
                        initVals={{
                          autoresize_bottom_margin: 0,
                          min_height: 125,
                        }}
                      />
                    </div>

                    {/* Meal Plan Attachments */}
                    {mealPlan.uploads.length > 0 && (
                      <div>
                        <p className="text-sm font-pt-sans text-gray-100 mb-2">Attachments</p>
                        <div className="flex gap-2 flex-wrap">
                          {mealPlan.uploads.map((upload, uploadIdx) => (
                            <div key={uploadIdx} className="relative">
                              <span className="bg-gray-10 text-12px text-gray-100 p-10px w-[90px] h-[90px] flex items-center">
                                <span className="w-full line-clamp-3">{upload.displayName}</span>
                              </span>
                              <CircleIconButton
                                className="absolute top-2 right-2"
                                IconComponent={XIcon}
                                iconSize="sm"
                                type="secondary"
                                onClick={() =>
                                  onMealPlanRemoveUpload(hotel.uuid, accommodation.uuid, mealPlan.uuid, uploadIdx)
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Add attachment button for meal plan */}
                    <div>
                      <FluidButton
                        onClick={async () => {
                          const result = await mealPlanUploadModalOps.requestConfirmation({
                            uploadType: 'file',
                            mealPlanUuid: mealPlan.uuid,
                          });
                          if (result) {
                            onMealPlanAddUpload(hotel.uuid, accommodation.uuid, mealPlan.uuid, result);
                          }
                        }}
                        type="secondary"
                      >
                        Attach File to Meal Plan
                      </FluidButton>
                    </div>
                  </div>
                </AccordionStackable>
              ))}
            </div>
          )}

          {/* Supplements */}
          {accommodation.supplements.length > 0 && (
            <div className="mb-4">
              <div>
                {accommodation.supplements.map(supplement => (
                  <AccordionStackable
                    key={supplement.uuid}
                    isOpen={openSupplements.includes(supplement.uuid)}
                    onToggle={isOpen => handleSupplementToggle(supplement.uuid, isOpen)}
                    renderSummary={({ isOpen }) => (
                      <span className="flex items-center justify-between w-full pr-4">
                        <span
                          className={classNames('text-base font-pt-sans', {
                            'font-bold': isOpen,
                            'font-medium': !isOpen,
                          })}
                        >
                          Supplement: {supplement.title}
                        </span>
                        <SlideToggle
                          id={`toggle-supplement-${supplement.uuid}`}
                          checked={supplement.isVisible}
                          onActivate={() =>
                            onSupplementVisibilityToggle(hotel.uuid, accommodation.uuid, supplement.uuid, true)
                          }
                          onDeactivate={() =>
                            onSupplementVisibilityToggle(hotel.uuid, accommodation.uuid, supplement.uuid, false)
                          }
                          labelClassName={!supplement.isVisible ? 'bg-gray-20' : 'bg-teal-40'}
                          buttonClassName={!supplement.isVisible ? 'bg-gray-100' : 'bg-teal-100'}
                          onClick={e => e.stopPropagation()}
                        />
                      </span>
                    )}
                  >
                    <div className="space-y-4">
                      <div className="relative">
                        <p className="text-sm font-pt-sans text-gray-100 mb-2">Description</p>
                        <Editor
                          handleEditorChange={content =>
                            onSupplementTertiaryTextChange(hotel.uuid, accommodation.uuid, supplement.uuid, content)
                          }
                          text={supplement.tertiaryText || ''}
                          options={[]}
                          placeHolder="Write supplement information..."
                          minHeight="125px"
                          initVals={{
                            autoresize_bottom_margin: 0,
                            min_height: 125,
                          }}
                        />
                      </div>
                    </div>
                  </AccordionStackable>
                ))}
              </div>

              {/* Supplements More Info */}
              <div className="mt-3 relative">
                <p className="text-sm font-pt-sans text-gray-100 mb-2">Supplements More Info</p>
                <Editor
                  handleEditorChange={content => onSupplementsMoreInfoChange(hotel.uuid, accommodation.uuid, content)}
                  text={accommodation.supplementsMoreInfo?.text || ''}
                  options={[]}
                  placeHolder="Write additional supplements information..."
                  minHeight="125px"
                  initVals={{
                    autoresize_bottom_margin: 0,
                    min_height: 125,
                  }}
                />
              </div>
            </div>
          )}

          {/* Additional Hotel Services */}
          {accommodation.additionalHotelServices.length > 0 && (
            <div className="mb-4">
              <div>
                {accommodation.additionalHotelServices.map(service => (
                  <AccordionStackable
                    key={service.uuid}
                    isOpen={openAdditionalServices.includes(service.uuid)}
                    onToggle={isOpen => handleAdditionalServiceToggle(service.uuid, isOpen)}
                    renderSummary={({ isOpen }) => (
                      <span className="flex items-center justify-between w-full pr-4">
                        <span
                          className={classNames('text-base font-pt-sans', {
                            'font-bold': isOpen,
                            'font-medium': !isOpen,
                          })}
                        >
                          Additional Hotel Service: {service.title}
                        </span>
                        <SlideToggle
                          id={`toggle-service-${service.uuid}`}
                          checked={service.isVisible}
                          onActivate={() =>
                            onAdditionalServiceVisibilityToggle(hotel.uuid, accommodation.uuid, service.uuid, true)
                          }
                          onDeactivate={() =>
                            onAdditionalServiceVisibilityToggle(hotel.uuid, accommodation.uuid, service.uuid, false)
                          }
                          labelClassName={!service.isVisible ? 'bg-gray-20' : 'bg-teal-40'}
                          buttonClassName={!service.isVisible ? 'bg-gray-100' : 'bg-teal-100'}
                          onClick={e => e.stopPropagation()}
                        />
                      </span>
                    )}
                  >
                    <div className="space-y-4 relative">
                      <div>
                        <p className="text-sm font-pt-sans text-gray-100 mb-2">Description</p>
                        <Editor
                          handleEditorChange={content =>
                            onAdditionalServiceTertiaryTextChange(hotel.uuid, accommodation.uuid, service.uuid, content)
                          }
                          text={service.tertiaryText || ''}
                          options={[]}
                          placeHolder="Write additional service information..."
                          minHeight="125px"
                          initVals={{
                            autoresize_bottom_margin: 0,
                            min_height: 125,
                          }}
                        />
                      </div>
                    </div>
                  </AccordionStackable>
                ))}
              </div>
              {/* Additional Services More Info */}
              <div className="mt-3 relative">
                <p className="text-sm font-pt-sans text-gray-100 mb-2">Additional Services More Info</p>
                <Editor
                  handleEditorChange={content =>
                    onAdditionalServicesMoreInfoChange(hotel.uuid, accommodation.uuid, content)
                  }
                  text={accommodation.additionalHotelServicesMoreInfo?.text || ''}
                  options={[]}
                  placeHolder="Write additional services information..."
                  minHeight="125px"
                  initVals={{
                    autoresize_bottom_margin: 0,
                    min_height: 125,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </AccordionStackable>

      {/* Upload Modals */}
      <VoucherAddUploadModal
        voucherUuid={voucherUuid}
        isOpen={accommodationUploadModalOps.isOpen}
        onClose={accommodationUploadModalOps.handleClose}
        uploadType={accommodationUploadModalOps.modalData?.uploadType || 'file'}
        handleConfirm={accommodationUploadModalOps.handleConfirm}
        modalTitle="Add File to Accommodation"
        productType="accommodation"
      />

      <VoucherAddUploadModal
        voucherUuid={voucherUuid}
        isOpen={mealPlanUploadModalOps.isOpen}
        onClose={mealPlanUploadModalOps.handleClose}
        uploadType={mealPlanUploadModalOps.modalData?.uploadType || 'file'}
        handleConfirm={mealPlanUploadModalOps.handleConfirm}
        modalTitle="Add File to Meal Plan"
        productType="meal plan"
      />
    </>
  );
};
